import { CryptoEvent } from 'matrix-js-sdk/src/crypto-api';
import { ClientEvent, MatrixEvent } from 'matrix-js-sdk/src/matrix';
import { useEffect, useRef } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';

interface UseCryptoStateSyncProps {
  refreshCurrentDeviceStatus: () => Promise<void>;
  checkForVerifiedDevices: () => Promise<void>;
  refreshSecurityCodeState?: () => void;
}

/** Coalesce burst crypto events (identity change + keys/query) into one refresh after SDK settles. */
const CRYPTO_SYNC_DEBOUNCE_MS = 50;

/**
 * Listens to Matrix crypto events and syncs device verification, verified devices list, and pin state.
 * Ensures all devices stay in sync when keys are reset, devices are verified, security code is created, etc.
 */
const useCryptoStateSync = ({
  refreshCurrentDeviceStatus,
  checkForVerifiedDevices,
  refreshSecurityCodeState,
}: UseCryptoStateSyncProps) => {
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!matrixClient) return;

    const runSync = () => {
      void refreshCurrentDeviceStatus();
      void checkForVerifiedDevices();
      if (refreshSecurityCodeState) refreshSecurityCodeState();
    };

    const scheduleCryptoSync = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        runSync();
      }, CRYPTO_SYNC_DEBOUNCE_MS);
    };

    const handleAccountData = (event: MatrixEvent) => {
      const eventType = event.getType();
      if (eventType === 'm.cross_signing.master' && refreshSecurityCodeState) refreshSecurityCodeState();
    };

    const events = [
      { event: CryptoEvent.KeysChanged, handler: scheduleCryptoSync },
      { event: CryptoEvent.UserTrustStatusChanged, handler: scheduleCryptoSync },
      { event: CryptoEvent.DevicesUpdated, handler: scheduleCryptoSync },
      { event: ClientEvent.AccountData, handler: handleAccountData },
    ];

    events.forEach(({ event, handler }) => {
      matrixClient.on(event, handler);
    });

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      events.forEach(({ event, handler }) => {
        matrixClient.off(event, handler);
      });
    };
  }, [matrixClient, refreshCurrentDeviceStatus, checkForVerifiedDevices, refreshSecurityCodeState]);
};

export default useCryptoStateSync;
